import Header from "../Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const statusBadgeClass = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "success" || normalized === "berhasil") {
        return "bg-green-100 text-green-700";
    }

    if (normalized === "pending") {
        return "bg-amber-100 text-amber-700";
    }

    if (normalized === "expired" || normalized === "failed") {
        return "bg-red-100 text-red-700";
    }

    return "bg-slate-100 text-slate-700";
};

export default function Index({ dailySales = [] }) {
    const totalOrders = dailySales.length;
    const totalSales = dailySales.reduce(
        (sum, sale) => sum + Number(sale.so_total_price || 0),
        0
    );
    const successfulSales = dailySales.filter((sale) => {
        const invoice = sale.invoices?.[0] ?? null;
        return Number(invoice?.payment?.payment_status) === 1;
    });
    const totalInvoiceItemValue = successfulSales.reduce((sum, sale) => {
        const items = Array.isArray(sale.invoices)
            ? sale.invoices.flatMap((invoice) =>
                  Array.isArray(invoice?.items) ? invoice.items : []
              )
            : [];

        return (
            sum +
            items.reduce(
                (itemSum, item) => itemSum + Number(item?.inv_item_value || 0),
                0
            )
        );
    }, 0);
    const totalPaymentFeeNominal = successfulSales.reduce((sum, sale) => {
        const items = Array.isArray(sale.invoices)
            ? sale.invoices.flatMap((invoice) =>
                  Array.isArray(invoice?.items) ? invoice.items : []
              )
            : [];

        return (
            sum +
            items.reduce(
                (itemSum, item) =>
                    itemSum + Number(item?.payment_fee_nominal || 0),
                0
            )
        );
    }, 0);
    const successPayments = successfulSales.length;
    const successMomo = dailySales.filter((sale) => {
        const invoice = sale.invoices?.[0] ?? null;
        return invoice?.status_momo_transfer_label === "success";
    }).length;

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div>
                            <h1 className="text-lg md:text-xl font-semibold text-foreground">
                                Dashboard
                            </h1>
                            <p className="text-sm text-muted-foreground-1">
                                Daily sales list from today&apos;s sales order data.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-5">
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Total Orders</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{totalOrders}</p>
                            </div>
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Total Sales</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{formatCurrency(totalSales)}</p>
                            </div>
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Payment Success</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{successPayments}</p>
                            </div>
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Momo Success</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{successMomo}</p>
                            </div>
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Total Item Value</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{formatCurrency(totalInvoiceItemValue)}</p>
                            </div>
                            <div className="p-4 bg-card border border-card-line rounded-xl shadow-2xs">
                                <p className="text-xs text-muted-foreground-1">Total Merchant Fee</p>
                                <p className="mt-2 text-xl font-semibold text-foreground">{formatCurrency(totalPaymentFeeNominal)}</p>
                            </div>
                        </div>

                        <div className="p-5 flex flex-col bg-card border border-card-line shadow-2xs rounded-xl">
                            <div className="pb-4 border-b border-card-header">
                                <h2 className="inline-block font-semibold text-foreground">
                                    Daily Sales
                                </h2>
                            </div>

                            <div className="overflow-x-auto mt-4">
                                <table className="min-w-full divide-y divide-table-line">
                                    <thead>
                                        <tr>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">
                                                Buyer
                                            </th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">
                                                Total Order
                                            </th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">
                                                Payment Status
                                            </th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">
                                                Momo Status
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-table-line">
                                        {dailySales.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="py-6 px-4 text-sm text-muted-foreground-2 text-center"
                                                >
                                                    No sales order data for today.
                                                </td>
                                            </tr>
                                        ) : (
                                            dailySales.map((sale) => {
                                                const invoice = sale.invoices?.[0] ?? null;
                                                const paymentStatus =
                                                    invoice?.payment?.payment_status_label ?? "-";
                                                const momoStatus =
                                                    invoice?.status_momo_transfer_label ?? "-";
                                                const buyerName =
                                                    sale.creator?.name?.trim() ||
                                                    invoice?.momo_nick_name?.trim() ||
                                                    "-";

                                                return (
                                                    <tr key={sale.so_id} className="hover:bg-surface">
                                                        <td className="py-3 px-4 text-sm text-foreground">
                                                            {buyerName}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                            {formatCurrency(sale.so_total_price)}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                            <span
                                                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(
                                                                    paymentStatus
                                                                )}`}
                                                            >
                                                                {paymentStatus}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                            <span
                                                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(
                                                                    momoStatus
                                                                )}`}
                                                            >
                                                                {momoStatus}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
